import { FACTIONS, type Faction } from './constants.ts';
import { ARENAS } from './arenas/index.ts';

/**
 * What a lobby is, and what may be asked of one.
 *
 * Kept in core and away from any screen, because every one of these rules has to
 * hold in two places that cannot see each other: a lobby screen offering choices
 * to somebody, and a server deciding whether to honour them. A dropdown with
 * five options is a suggestion; a server that accepts any number sent to it is
 * a server that will one day be running a match with a two-thousand-per-cent
 * experience scale because somebody edited a value in a console.
 */

/** How a map is played. The map decides — there is no separate mode to pick. */
export type GameMode = 'shrines';

export const MODE_NAMES: Record<GameMode, string> = {
  shrines: 'Shrine Dominance',
};

/**
 * Which mode each map is played under.
 *
 * Bound to the map rather than chosen beside it, because the two are not
 * independent: the Cathedral has three shrines and eight pools, and playing
 * anything but Shrine Dominance on it would mean ignoring most of the level.
 * A map that wants a different mode is a different map.
 */
const MAP_MODES: Record<string, GameMode> = {
  'great-cathedral': 'shrines',
  cathedral: 'shrines',
};

export function modeFor(mapId: string): GameMode {
  return MAP_MODES[mapId] ?? 'shrines';
}

export interface MapChoice {
  id: string;
  name: string;
  mode: GameMode;
}

export function playableMaps(): MapChoice[] {
  return ARENAS.map((a) => ({ id: a.id, name: a.name, mode: modeFor(a.id) }));
}

// ── The dials ────────────────────────────────────────────────────────────────

/**
 * Every option a lobby offers, as the only values that exist.
 *
 * Written as lists rather than as ranges so the screen and the validator cannot
 * disagree: the dropdown is built from the same array the server checks against,
 * and adding an option is one edit rather than two that must be kept in step.
 */
/**
 * Placeholder roster names, cycled. Real ones can come with real behaviour.
 *
 * In core rather than in `main.ts` because a hosted lobby's bots are seated by
 * the *server*, and a server that invented its own names would give a player
 * different opponents depending on where the match was running.
 */
export const BOT_NAMES = [
  'Aldwin', 'Brannoc', 'Cerys', 'Dunstan', 'Eilith',
  'Faramund', 'Gwyneth', 'Hallr',
] as const;

export const BOT_CLASSES = ['magician', 'cleric', 'mentalist', 'arcanist'] as const;

/** The most bots a side may be given. Enough to fill a team twice over. */
export const MAX_BOTS_PER_SIDE = 8;

export const XP_SCALES = [0.5, 0.75, 1, 1.5, 2] as const;
export const TIME_LIMITS = [10, 12, 15, 20, 25] as const;
export const BEST_OF = [1, 3, 5] as const;

/** Two is a duel and twenty-four is three full sides. */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 24;

export const LOBBY_NAME_MAX = 32;
export const LOBBY_NAME_MIN = 3;

export interface LobbySettings {
  name: string;
  /** Maps in rotation, in order. Never empty. */
  mapPool: string[];
  bestOf: number;
  maxPlayers: number;
  xpScale: number;
  /** Round length in minutes. */
  timeLimit: number;
  /**
   * Whether levels earned in one round are kept into the next.
   *
   * A toggle rather than a decision, because the two answers suit different
   * rooms and neither is wrong. Carried, a side that wins the first round is
   * stronger in the second and a series can snowball — which is a real cost and
   * also *the fun*, in a lobby that is there to muck about and reach levels
   * nobody sees in a single round. Reset, every round starts even, which is what
   * a room playing seriously wants.
   *
   * The point is that a host chooses. A game that enforces a competitive mindset
   * on people who did not come for one has simply chosen for them.
   */
  carryXp: boolean;
}

export function defaultSettings(hostName = 'Someone'): LobbySettings {
  return {
    name: `${hostName}'s lobby`.slice(0, LOBBY_NAME_MAX),
    mapPool: [ARENAS[0]!.id],
    bestOf: 3,
    maxPlayers: 12,
    xpScale: 1,
    timeLimit: 15,
    carryXp: true,
  };
}

// ── Names ────────────────────────────────────────────────────────────────────

/**
 * Words a lobby name may not contain.
 *
 * Deliberately short and deliberately not clever. A long list catches ordinary
 * words with unfortunate substrings — the Scunthorpe problem, which every
 * attempt at this rediscovers — and an arms race against people determined to
 * get something past it is not winnable and not worth entering. This stops the
 * lazy case; a report button is what handles the rest, and it is the only thing
 * that ever really does.
 */
const BARRED = [
  'fuck', 'shit', 'cunt', 'nigger', 'faggot', 'rape', 'nazi',
];

/**
 * Cleans a lobby name, or explains why it cannot be used.
 *
 * Matched on word boundaries rather than substrings, so Scunthorpe and Penistone
 * survive. Also strips control characters and collapses runs of whitespace,
 * which is the other half of the job — a name with forty spaces in it wrecks a
 * table far more reliably than a rude word does.
 */
export function checkLobbyName(raw: unknown): { name: string } | { error: string } {
  if (typeof raw !== 'string') return { error: 'A lobby needs a name' };

  const name = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LOBBY_NAME_MAX);

  if (name.length < LOBBY_NAME_MIN) {
    return { error: `At least ${LOBBY_NAME_MIN} characters` };
  }

  const words = name.toLowerCase().split(/[^a-z]+/);
  if (words.some((w) => BARRED.includes(w))) {
    return { error: 'Pick another name' };
  }

  return { name };
}

/**
 * Forces settings into the shape a room can actually be built from.
 *
 * Clamped rather than rejected wherever there is an obvious right answer, for
 * the same reason inputs are: a rejection is a dead end and a clamp is a game
 * that starts. The exceptions are a name that cannot be used and a map pool with
 * nothing in it, neither of which has a sensible substitute.
 */
export function checkSettings(raw: Partial<LobbySettings>): {
  settings: LobbySettings;
} | { error: string } {
  const named = checkLobbyName(raw.name);
  if ('error' in named) return named;

  const known = new Set(ARENAS.map((a) => a.id));
  const mapPool = (Array.isArray(raw.mapPool) ? raw.mapPool : []).filter((m) => known.has(m));
  if (!mapPool.length) return { error: 'Choose at least one map' };

  const nearest = (options: readonly number[], value: unknown, fallback: number): number => {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return options.reduce((best, o) => (Math.abs(o - n) < Math.abs(best - n) ? o : best));
  };

  return {
    settings: {
      name: named.name,
      mapPool,
      bestOf: nearest(BEST_OF, raw.bestOf, 3),
      maxPlayers: Math.round(
        Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, Number(raw.maxPlayers) || 12)),
      ),
      xpScale: nearest(XP_SCALES, raw.xpScale, 1),
      timeLimit: nearest(TIME_LIMITS, raw.timeLimit, 15),
      carryXp: raw.carryXp !== false,
    },
  };
}

// ── Sides ────────────────────────────────────────────────────────────────────

/**
 * Which side somebody joining should be put on.
 *
 * The emptiest, and on a tie the earliest in the standard order — which keeps it
 * predictable rather than random. Predictable matters more than it sounds: two
 * friends joining together should land together, and with a coin toss they have
 * an even chance of being made enemies before either has clicked anything.
 *
 * Assigned rather than chosen because a lobby that lets everybody pick produces
 * eleven on one side and one on another, and the one is not having a good time.
 * Somebody who wants to switch can, and *that* is where a check belongs.
 */
export function fullestFirst(counts: Record<Faction, number>): Faction[] {
  return [...FACTIONS].sort((a, b) => counts[b] - counts[a]);
}

export function balanceInto(
  counts: Record<Faction, number>,
  points: Record<Faction, number> = { order: 0, balance: 0, chaos: 0 },
): Faction {
  /*
   * Fewest mages, then fewest points, then the standard order.
   *
   * No coin toss at the end. A random tie-break costs something real that is
   * easy to miss: two friends joining seconds apart have an even chance of being
   * made enemies before either has clicked anything. A stable rule is duller and
   * always right, and if variety across *matches* is wanted it belongs in a
   * per-match seed rather than in a per-join dice roll.
   *
   * Points second because the side that is behind is the side that needs the
   * help, and a drop-in is the only help a losing team is going to get.
   */
  let best: Faction = FACTIONS[0]!;
  for (const f of FACTIONS) {
    if (counts[f] < counts[best]) { best = f; continue; }
    if (counts[f] === counts[best] && points[f] < points[best]) best = f;
  }
  return best;
}

/**
 * How far out of balance the sides may get before a switch is refused.
 *
 * Loose on purpose. A strict rule produces a lobby that is even and full of
 * people who could not sit with the friend they arrived with, which is a worse
 * room than a slightly lopsided one. Three is enough slack for a group to move
 * together and still short of the point where a match stops being one.
 */
export const BALANCE_SLACK = 3;

/**
 * Whether somebody may move to a side they picked themselves.
 *
 * Only the *spread* is checked — largest side against smallest, after the move —
 * which is the whole rule and deliberately not more than that. Earlier versions
 * measured against an average or against the side being left, and both ended up
 * refusing switches that no reasonable person would object to while being hard
 * to explain to somebody staring at a greyed-out button.
 */
export function maySwitch(
  counts: Record<Faction, number>, from: Faction, to: Faction,
): boolean {
  if (from === to) return false;
  if (counts[from] <= 0) return false;

  const spread = (heads: Record<Faction, number>): number => {
    const sizes = FACTIONS.map((f) => heads[f]);
    return Math.max(...sizes) - Math.min(...sizes);
  };

  const after = { ...counts, [from]: counts[from] - 1, [to]: counts[to] + 1 };

  /*
   * A move that makes things better is always allowed.
   *
   * Checking the resulting spread alone refused the most obviously reasonable
   * switch there is: five on one side and none on the other, and crossing to the
   * empty one leaves a spread of four, which is over the slack — so the rule
   * blocked somebody trying to *fix* the imbalance it existed to prevent. An
   * empty side can never be the problem, because it has nobody on it.
   *
   * So the test is the spread against what it already was, and the slack is only
   * a floor beneath that.
   */
  return spread(after) <= Math.max(BALANCE_SLACK, spread(counts));
}

// ── What a list row shows ────────────────────────────────────────────────────

/**
 * One line of the Join Match list.
 *
 * The shape a server sends and a screen renders, named here so the two cannot
 * drift. Everything on it is something a player decides by: how full, what map,
 * how long is left, and whether the rules are the usual ones.
 */
export interface LobbySummary {
  id: string;
  name: string;
  players: number;
  maxPlayers: number;
  /** The map being played *now*, not the pool. */
  map: string;
  mode: GameMode;
  xpScale: number;
  /** Seconds elapsed and the round's length, both in seconds. */
  elapsed: number;
  limit: number;
  /** Rounds played on a map before it rotates: 1, 3 or 5. */
  bestOf: number;
  /** Which round is being played, counting from one. */
  round: number;
  /** Rounds each side has taken. */
  wins: Record<Faction, number>;
  /**
   * Whether there is a match happening in there.
   *
   * A joiner is entitled to know. Walking into a room that has been going for
   * ten minutes is a different decision from joining one that has not started,
   * and a lobby screen that shows the same countdown either way is telling
   * somebody the match is about to begin when it began before they arrived.
   */
  inProgress: boolean;
  /**
   * Who is in there: names and sides, no positions.
   *
   * The same rule the in-match roster follows — identity is not the secret, and
   * a join screen that cannot say who is playing is asking somebody to pick a
   * side blind.
   */
  roster: {
    name: string; faction: Faction; cls: string; level: number; bot: boolean;
  }[];
  /** Whether it is between rounds, which is when joining is painless. */
  inWarmup: boolean;
  locked: boolean;
}

/** `7:42 / 15:00`, which is the only format the list ever wants. */
export function formatClockPair(elapsed: number, limit: number): string {
  const mmss = (s: number): string => {
    const whole = Math.max(0, Math.floor(s));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
  };
  return `${mmss(elapsed)} / ${mmss(limit)}`;
}

/** Whether a lobby will take somebody right now. */
export function joinable(row: LobbySummary): boolean {
  return !row.locked && row.players < row.maxPlayers;
}


// ── Waiting for a match ──────────────────────────────────────────────────────

/**
 * How long a lobby waits before starting itself.
 *
 * Two minutes, and a timer rather than a ready-check alone. A pure ready-up lets
 * one idle player hold nine others hostage, which is the single most reliable
 * way to empty a room; a pure timer makes everybody sit through it when they are
 * all plainly waiting to go. So the timer is the floor and readiness is the
 * accelerator.
 *
 * It is also the breather. Between rounds people want to say something about the
 * one that just ended, change class, and get a drink — and a lobby that starts
 * the moment the scoreboard clears gives them nowhere to do any of it.
 */
export const LOBBY_SECONDS = 120;

/** Once everybody is ready, this is the grace before the map loads. */
export const LOBBY_HURRY = 5;

export interface LobbyMember {
  id: string;
  name: string;
  faction: Faction;
  /** Null until they have chosen; a class is required before a match starts. */
  cls: string | null;
  /** Shown beside the class, because a levelled lobby is a different lobby. */
  level: number;
  ready: boolean;
  bot: boolean;
  /** Ticks up as people arrive. Decides who inherits a host that leaves. */
  joinedAt: number;
}

/**
 * Everything the lobby screen draws, in one object.
 *
 * Named here rather than in the screen because it is what a server will send:
 * the screen is a function of this and nothing else, which is what lets it be
 * built and looked at before there is anything on the other end.
 */
export interface LobbyView {
  name: string;
  /**
   * Set when this screen is a waiting room for a match already under way.
   *
   * The screen shows the same roster and the same score either way; what changes
   * is what the clock means. Waiting for a match to start, it is a countdown.
   * Waiting to be let into one, it is how long the round has left.
   */
  inProgress?: boolean;
  /** Which round of the set is next, and how long the set is. */
  round: number;
  bestOf: number;
  map: string;
  /** What follows this one, if the pool has more than one map in it. */
  nextMap: string | null;
  secondsLeft: number;
  wins: Record<Faction, number>;
  members: LobbyMember[];
  /** Whose screen this is, and whether they may change the room's mind. */
  youId: string;
  isHost: boolean;
  maxPlayers: number;
}

/** Heads per side, which several rules and the screen all want. */
export function headcount(members: readonly LobbyMember[]): Record<Faction, number> {
  const heads: Record<Faction, number> = { order: 0, balance: 0, chaos: 0 };
  for (const m of members) heads[m.faction] += 1;
  return heads;
}

/**
 * What a mage's row says after their name: level and discipline, as `7ar`.
 *
 * The same four characters the in-match roster uses, for the same reason —
 * class says what somebody is *for* and level says how much of it they can do,
 * and in a lobby where experience carries the second is how you tell whether you
 * are joining a fresh match or a rout in progress.
 */
export function memberBadge(m: LobbyMember): string {
  if (!m.cls) return '—';
  return `${m.level}${m.cls.slice(0, 2).toLowerCase()}`;
}

/**
 * Whether the countdown should be cut short.
 *
 * Bots are not asked — they are always ready, and waiting on one would mean a
 * lobby full of them never starting. A lobby with nobody human in it is a
 * persistent server idling, and that is what the timer is for.
 */
export function everyoneReady(members: readonly LobbyMember[]): boolean {
  const people = members.filter((m) => !m.bot);
  if (!people.length) return false;
  return people.every((m) => m.ready && m.cls !== null);
}

/**
 * Seconds left, given how long the room has waited and how long it has been
 * ready.
 *
 * `readyFor` is what makes the hurry a *deadline* rather than a ceiling. Without
 * it this returned `min(full, 5)` — which is five seconds, then five seconds
 * again, then five seconds again, for as long as anybody cared to watch. The
 * clock read correctly and never moved, and the match never started.
 */
export function countdown(
  waitedFor: number, members: readonly LobbyMember[], readyFor = 0,
): number {
  const full = Math.max(0, LOBBY_SECONDS - waitedFor);
  if (!everyoneReady(members)) return full;
  return Math.max(0, Math.min(full, LOBBY_HURRY - readyFor));
}

/**
 * Who runs a lobby, if anybody does.
 *
 * A persistent server has no host — it has rules, and they were set before
 * anybody arrived and outlast everybody who did. Elsewhere the host is simply
 * whoever has been here longest, so losing one is a handover rather than an
 * event: the room does not pause, nothing is voted on, and the next person in
 * line finds out they can change the map.
 */
export function hostOf(
  members: readonly LobbyMember[], persistent = false,
): string | null {
  if (persistent) return null;
  const people = members.filter((m) => !m.bot);
  if (!people.length) return null;
  return people.reduce((first, m) => (m.joinedAt < first.joinedAt ? m : first)).id;
}

/**
 * Which bot to remove to make room for a person, if any may be.
 *
 * Only ever in a lobby. During a live match a bot is a mage with a position, a
 * level and possibly a pool it is holding, and deleting one to seat a latecomer
 * changes the match for everybody already in it — so a live room is full when it
 * is full, and the person waits for the round to end.
 *
 * The bot taken is from the largest side, so seating somebody never makes the
 * imbalance worse than it already was.
 */
export function bumpableBot(
  members: readonly LobbyMember[], live: boolean,
): string | null {
  if (live) return null;

  const bots = members.filter((m) => m.bot);
  if (!bots.length) return null;

  const heads: Record<Faction, number> = { order: 0, balance: 0, chaos: 0 };
  for (const m of members) heads[m.faction] += 1;

  const fullest = fullestFirst(heads);
  for (const faction of fullest) {
    const victim = bots.find((b) => b.faction === faction);
    if (victim) return victim.id;
  }
  return bots[0]!.id;
}

/**
 * Whether somebody may join at all, and what has to happen first.
 *
 * Three answers rather than two, because "no" and "not until a bot leaves" are
 * different things to a person watching a list: one means look elsewhere and the
 * other means click.
 */
export function admit(
  members: readonly LobbyMember[], capacity: number, live: boolean,
): { ok: true; bump?: string } | { ok: false; why: string } {
  if (members.length < capacity) return { ok: true };

  const bump = bumpableBot(members, live);
  if (bump) return { ok: true, bump };

  return {
    ok: false,
    why: live ? 'Match is full' : 'Lobby is full',
  };
}
